import { css } from 'aphrodite';
import * as React from 'react';
import { IAIAgentTemplate } from '../../../../extViewmodels/sdk/models/AI';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../../web/components-v2/Alert';
import { Button } from '../../../../web/components-v2/Button';
import { Field, FieldDescription, FieldLabel, FieldTitle } from '../../../../web/components-v2/Field';
import { Input } from '../../../../web/components-v2/Input';
import { NumberedScaleInput } from '../../../../web/components-v2/NumberedScaleInput';
import { TextArea } from '../../../../web/components-v2/TextArea';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { CardFeedback } from './CardFeedback';
import { CardMarkdown } from './CardMarkdown';
import { type IRealCallTurn, isRepSpeaker, realCallMarkdown } from './labLogic';
import { styles } from './styles';
import { useCoachLabReview } from './useCoachLabReview';

const ShouldHaveSaid = ({ turn, onSave }: { turn: IRealCallTurn; onSave: (text: string) => void }) => {
	const [draft, setDraft] = React.useState(turn.shouldHaveSaid ?? '');
	return (
		<div className={css(styles.actions)}>
			<Input
				placeholder='What the card should have told the rep to say'
				value={draft}
				onChange={e => setDraft(e.target.value)}
				onBlur={() => onSave(draft)}
				onKeyDown={e => e.key === 'Enter' && onSave(draft)}
			/>
		</div>
	);
};

export const CoachLabRealCall = ({
	conversationId,
	sandboxCoach,
	onDone,
	onSave,
	onUnsavedChange,
}: {
	conversationId: string;
	sandboxCoach?: IAIAgentTemplate;
	onDone: () => void;
	/** Adds the review to the sitting's list; the manager downloads the list once, at the end. */
	onSave: (session: { label: string; markdown: string }) => void;
	/** True while the manager has feedback on this call that is not saved yet. */
	onUnsavedChange: (unsaved: boolean) => void;
}) => {
	const review = useCoachLabReview(conversationId, sandboxCoach?.id);
	const userSession = useUserSession();
	const { meta, turns } = review;
	const [rating, setRating] = React.useState<string>();
	const [overall, setOverall] = React.useState('');
	const [confirmingBack, setConfirmingBack] = React.useState(false);
	const ratingLabelId = React.useId();
	const hasFeedback = turns.some(t => t.notes.length > 0 || !!t.shouldHaveSaid) || !!overall.trim();
	const unsaved = hasFeedback;

	React.useEffect(() => onUnsavedChange(unsaved), [unsaved, onUnsavedChange]);

	const saveMutation = useMutation({
		mutationFn: async () => {
			if (!meta) return;
			const markdown = realCallMarkdown({
				buildId: await review.buildId(),
				callDate: meta.callDate,
				companyId: meta.companyId,
				conversationId,
				leadLabel: meta.leadLabel,
				liveCoach: meta.liveCoach,
				manager: userSession.user ? getDisplayName(userSession.user) : 'unknown',
				overall,
				rating: rating ? Number(rating) : undefined,
				rep: meta.rep,
				sandboxCoach: sandboxCoach ? { id: sandboxCoach.id, name: sandboxCoach.name } : undefined,
				turns,
			});
			onSave({ label: meta.leadLabel, markdown });
		},
		onError: () => toast.error('Could not save the review. Try again.'),
		onSuccess: onDone,
	});

	if (review.phase === 'loading') return <LoadingSpinner />;
	if (review.phase !== 'ready' || !meta) {
		return (
			<Alert tone='danger'>
				<AlertTitle>
					{review.phase === 'empty' ? 'This call has no coach cards to review.' : 'Could not load that call.'}
				</AlertTitle>
				<AlertDescription>
					Check the link or conversation id. It should be a coached call from AI Coaching Review or the call log.
				</AlertDescription>
			</Alert>
		);
	}

	return (
		<>
			<div className={css(styles.callHeader)}>
				<div>
					<h2 className={css(styles.callTitle)}>Real call with {meta.leadLabel}</h2>
					<p className={css(styles.help)}>
						{meta.rep}
						{meta.callDate ? `, ${meta.callDate}` : ''}. Live coach: {meta.liveCoach}.
					</p>
				</div>
				<Button variant='secondary' onClick={() => (unsaved ? setConfirmingBack(true) : onDone())}>
					Back to Coach Lab
				</Button>
			</div>
			{confirmingBack ? (
				<Alert tone='warning'>
					<AlertTitle>This review isn't downloaded yet</AlertTitle>
					<AlertDescription>If you go back now, your notes on this call are lost.</AlertDescription>
					<AlertActions>
						<Button variant='outline' size='sm' onClick={() => setConfirmingBack(false)}>
							Stay
						</Button>
						<Button variant='outline' size='sm' onClick={onDone}>
							Go back without it
						</Button>
					</AlertActions>
				</Alert>
			) : null}
			<p className={css(styles.intro)}>
				These are the cards {meta.rep} saw on this call, with what was said around each one. Mark the cards you would
				change.{' '}
				{sandboxCoach
					? `"What would my coach say here?" asks ${sandboxCoach.name} for its card at that moment of the same call, so you can see whether your version already does better.`
					: 'Pick your LAB coach in step 1 to compare its card with the live one at any moment of the call.'}
			</p>

			<div className={css(styles.transcript)}>
				{turns.map(turn => (
					<React.Fragment key={turn.number}>
						{turn.lines.map((line, i) => (
							<div key={i} className={css(isRepSpeaker(line.speaker) ? styles.repLine : styles.prospectLine)}>
								<span className={css(styles.speaker)}>{isRepSpeaker(line.speaker) ? meta.rep : 'Prospect'}</span>
								{line.text}
							</div>
						))}
						<div className={css(turn.sandboxCard !== undefined && styles.compareRow)}>
							<div className={css(styles.card)}>
								<span className={css(styles.cardLabel)}>Live coach card {turn.number}</span>
								<CardMarkdown>{turn.card}</CardMarkdown>
								<CardFeedback
									notes={turn.notes}
									editable
									kinds={['good', 'bad', 'note']}
									prompt='What do you think of this card?'
									onAdd={(kind, text) => review.addNote(turn.number, kind, text)}
									onRemove={index => review.removeNote(turn.number, index)}
								/>
								<ShouldHaveSaid turn={turn} onSave={text => review.setShouldHaveSaid(turn.number, text)} />
								{sandboxCoach ? (
									<div className={css(styles.actions)}>
										<Button
											variant='secondary'
											size='sm'
											isLoading={review.comparing.includes(turn.number)}
											disabled={review.comparing.includes(turn.number)}
											onClick={() => review.compare(turn.number)}
										>
											{turn.sandboxCard ? 'Ask my coach again' : 'What would my coach say here?'}
										</Button>
									</div>
								) : null}
							</div>
							{turn.sandboxCard !== undefined ? (
								<div className={css(styles.card, styles.sandboxCard)}>
									<span className={css(styles.cardLabel)}>{sandboxCoach?.name ?? 'Your coach'}</span>
									{turn.sandboxCard ? <CardMarkdown>{turn.sandboxCard}</CardMarkdown> : <LoadingSpinner />}
								</div>
							) : null}
						</div>
					</React.Fragment>
				))}
			</div>

			<section className={css(styles.step)}>
				<h2 className={css(styles.stepTitle)}>Save your review</h2>
				<Field>
					<FieldTitle id={ratingLabelId}>How good was the live coaching on this call? (optional)</FieldTitle>
					<NumberedScaleInput
						min={1}
						max={5}
						value={rating}
						onValueChange={setRating}
						lowLabel='Poor'
						highLabel='Great'
						ariaLabelledby={ratingLabelId}
					/>
				</Field>
				<Field>
					<FieldLabel htmlFor='coach-lab-real-overall'>What should the coach do differently? (optional)</FieldLabel>
					<TextArea
						styles={[styles.resizableText]}
						id='coach-lab-real-overall'
						placeholder='The pattern you saw across these cards'
						value={overall}
						onChange={e => setOverall(e.target.value)}
					/>
					<FieldDescription>
						Saving adds this review to Calls this sitting, with the live cards, your coach's cards where you asked for
						them, and your notes. Download it with the rest of the sitting.
					</FieldDescription>
				</Field>
				<div className={css(styles.actions)}>
					<Button
						variant='primary'
						isLoading={saveMutation.isPending}
						disabled={saveMutation.isPending}
						onClick={() => saveMutation.mutate()}
					>
						Save review
					</Button>
				</div>
			</section>
		</>
	);
};
